import * as React from "react";
import { FirmInstruction } from "../../../../../../../Wrap/Models/Models/Company/FirmInstruction";
import { SectionHeader } from "../../../../../../Dashboard/Components/Clients/NewBusinessWizard/Components/SharedComponents";
import { Table } from "../../../../../../Shared/Table/Table";
import { BankAccount } from "../../../../../../../Wrap/Models/Domains/BankAccount/BankAccount";
import { NewBankAccount } from "./NewBankAccount";
import { BankAccountOwner } from "../../../../../../../Wrap/Constants/Enums/Domain/BankAccountOwner";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { ValidatorContents } from "../../../../../../Dashboard/Common/ValidatorHelper";
import { FirmType } from "../../../../../../../Wrap/Constants/Enums/Domain/FirmType";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";

interface IBankDetailProps extends IProcessComponentProps {
    data: FirmInstruction;
    owner: BankAccountOwner;
    onChange: (changes) => void;
    onRef: (ref) => void;
    isReview: boolean;
}

interface IBankDetailState {
    currentBankAccount: BankAccount;
    isEdit: number;
    displayMaxNumberAlert: boolean;
    displayRequiredAlert: boolean;
    displayNeedDefault: boolean;
    displaySaveBeforeNextAlert: boolean;
}

export class BankDetail extends ProcessComponent<IBankDetailProps, IBankDetailState> {
    constructor(props: IBankDetailProps) {
        super(props);
        this.state = {
            currentBankAccount: null,
            isEdit: null,
            displayMaxNumberAlert: false,
            displayRequiredAlert: this.shouldDisplayRequiredAlert(this.props.data.BankAccounts.length),
            displayNeedDefault: this.shouldDisplayIsDefault(props.data.BankAccounts),
            displaySaveBeforeNextAlert: false,
        };
    }

    componentDidMount() {
        super.componentDidMount();
        this.props.onRef(this);
    }

    isValid(): boolean {
        return (
            !this.shouldDisplayRequiredAlert(this.props.data.BankAccounts.length) &&
            !this.shouldDisplayIsDefault(this.props.data.BankAccounts) &&
            this.shouldSaveOrCancelEditBankAccountBeforeNext()
        );
    }

    private shouldDisplayIsDefault(bankAccounts: BankAccount[]): boolean {
        let flag = false;
        if (bankAccounts && bankAccounts.length > 0) {
            const hasDefault = bankAccounts.filter((ele) => ele.IsDefault == true)[0];
            return (flag = hasDefault ? false : true);
        }
        return flag;
    }

    private shouldDisplayRequiredAlert(bankAccountsLength: number): boolean {
        return this.props.data.FirmType == FirmType.DirectlyAuthorised && bankAccountsLength == 0;
    }

    private shouldSaveOrCancelEditBankAccountBeforeNext(): boolean {
        if (this.state.currentBankAccount !== null) {
            this.setState({
                displaySaveBeforeNextAlert: true,
            });
            return false;
        }
        return true;
    }

    private getEmptyBankAccount(): BankAccount {
        const len = this.props.data.BankAccounts && this.props.data.BankAccounts.length;
        return {
            AccountNumber: null,
            AccountName: null,
            BankDetails: { Name: null },
            IsDefault: len == 0,
            Owner: this.props.owner,
        };
    }

    private addBankAccount() {
        if (this.props.data.BankAccounts.length >= ValidatorContents.MaxBankAccountsNumber) {
            this.setState({
                currentBankAccount: null,
                isEdit: null,
                displayMaxNumberAlert: true,
            });
        } else {
            this.setState({
                currentBankAccount: this.getEmptyBankAccount(),
                isEdit: null,
                displayMaxNumberAlert: false,
                displayRequiredAlert: false,
            });
        }
    }

    private onAccountChange(changes) {
        const { currentBankAccount } = this.state;
        const _current = JSON.parse(JSON.stringify(currentBankAccount));
        for (const key in changes) {
            _current[key] = changes[key];
        }
        this.setState({ currentBankAccount: _current });
    }

    render() {
        const { data } = this.props;
        return (
            <>
                {SectionHeader("Bank details")}
                <div className="row narrow-gutter row-right vspace-24">
                    <div className="col-auto"></div>
                    <div className="col-auto">
                        <button
                            className="btn small secondary"
                            onClick={() => this.addBankAccount()}
                            disabled={this.props.isReview}
                        >
                            Add bank account
                        </button>
                    </div>
                </div>
                <Table
                    data={this.props.data.BankAccounts}
                    col={[
                        { title: "Account reference", key: "AccountReference" },
                        { title: "Account name", key: "AccountName" },
                        { title: "Bank name", key: "BankDetails.Name" },
                        { title: "Sort code", key: "SortCode" },
                        { title: "Account number", key: "AccountNumber" },
                        {
                            title: "Is default",
                            key: "IsDefault",
                            render: (ele, row, index) => {
                                return <span>{ele ? "Yes" : "No"}</span>;
                            },
                        },
                        {
                            title: "",
                            render: (ele, row, index) => {
                                return (
                                    <>
                                        <button
                                            className={
                                                this.props.isReview
                                                    ? "btn secondary mr-10"
                                                    : "edit-circle-icon-button label-sr-only mr-2"
                                            }
                                            title={this.props.isReview ? "" : "Edit"}
                                            onClick={() => this.setState({ currentBankAccount: row, isEdit: index })}
                                        >
                                            {this.props.isReview ? null : (
                                                <span className="icon" aria-hidden="true"></span>
                                            )}
                                            {this.props.isReview ? "View" : <span className="text">Edit</span>}
                                        </button>

                                        <button
                                            className="remove-button label-sr-only"
                                            title="Delete"
                                            disabled={this.props.isReview}
                                            onClick={() => {
                                                const _accounts = data.BankAccounts;
                                                _accounts.splice(index, 1);
                                                this.props.onChange({ BankAccounts: _accounts });
                                                if (this.state.isEdit && this.state.isEdit == index) {
                                                    this.setState({
                                                        currentBankAccount: null,
                                                        isEdit: null,
                                                        displayRequiredAlert: this.shouldDisplayRequiredAlert(
                                                            _accounts.length
                                                        ),
                                                        displayNeedDefault: this.shouldDisplayIsDefault(_accounts),
                                                    });
                                                } else {
                                                    this.setState({
                                                        displayRequiredAlert: this.shouldDisplayRequiredAlert(
                                                            _accounts.length
                                                        ),
                                                        displayNeedDefault: this.shouldDisplayIsDefault(_accounts),
                                                    });
                                                }
                                            }}
                                        >
                                            <span className="icon" aria-hidden="true"></span>
                                            <span className="text">Delete</span>
                                        </button>
                                    </>
                                );
                            },
                        },
                    ]}
                />
                {this.renderAlert()}
                {this.state.currentBankAccount && (
                    <div className="">
                        <div className="col-xs-12 col-sm-7">
                            <NewBankAccount
                                hasDefault={
                                    data.BankAccounts.filter((ele, index) => {
                                        return index !== this.state.isEdit && ele.IsDefault == true;
                                    }).length > 0
                                }
                                isReview={this.props.isReview}
                                data={this.state.currentBankAccount}
                                onChange={(changes) => this.onAccountChange(changes)}
                                onClickSave={() => {
                                    let _accounts = [];
                                    if (this.state.isEdit !== 0 && !this.state.isEdit) {
                                        _accounts = data.BankAccounts.concat(this.state.currentBankAccount);
                                    } else {
                                        _accounts = data.BankAccounts;
                                        _accounts[this.state.isEdit] = this.state.currentBankAccount;
                                    }
                                    this.setState({
                                        currentBankAccount: null,
                                        isEdit: null,
                                        displayRequiredAlert: false,
                                        displayNeedDefault: this.shouldDisplayIsDefault(_accounts),
                                    });
                                    this.props.onChange({ BankAccounts: _accounts });
                                }}
                                onRef={() => {}}
                                onClickCancel={() => this.setState({ currentBankAccount: null })}
                            />
                        </div>
                    </div>
                )}
            </>
        );
    }

    renderAlert() {
        if (this.state.displayMaxNumberAlert) {
            return <Alert alertType={AlertType.Error} message="Can only have 10 bank accounts" />;
        } else if (this.state.displayRequiredAlert) {
            return (
                <Alert alertType={AlertType.Error} message="Directly Authorised Firm need at least 1 bank account" />
            );
        } else if (this.state.displayNeedDefault) {
            return <Alert alertType={AlertType.Error} message="Must have set one bank account as default" />;
        } else if (this.state.displaySaveBeforeNextAlert) {
            return <Alert alertType={AlertType.Warning} message="Must save or cancel edit bank account first" />;
        }
    }
}
